import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiIndicatorPeriod } from './indicator-card.types';

export type { UiIndicatorPeriod };

interface UiIndicatorBar extends UiIndicatorPeriod {
  readonly height: number;
  readonly current: boolean;
}

/**
 * Molécula: indicador financiero con variación mensual y tendencia de cuatro
 * periodos (mes actual y tres meses anteriores).
 */
@Component({
  selector: 'ui-indicator-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent],
  templateUrl: './indicator-card.component.html',
  styleUrl: './indicator-card.component.scss',
  host: { class: 'ui-indicator-card-host' },
})
export class UiIndicatorCardComponent {
  readonly title = input.required<string>();
  readonly amount = input.required<string>();
  readonly variation = input.required<number>();
  readonly periods = input.required<readonly UiIndicatorPeriod[]>();
  readonly showChart = input(true);
  readonly showMore = input(true);
  readonly moreLabel = input('Ver más');
  readonly variationPeriodLabel = input('mes anterior');
  /** Hace clicable toda la card y emite `cardClick`. Default: `false`. */
  readonly interactive = input(false);

  readonly moreClick = output<MouseEvent>();
  readonly cardClick = output<void>();

  protected readonly hoveredIndex = signal<number | null>(null);
  protected onCardClick(event: Event): void {
    if (!this.interactive()) return;
    const target = event.target;
    if (target instanceof Element && target.closest('button')) return;
    this.cardClick.emit();
  }

  protected onCardKeydown(event: Event): void {
    if (!this.interactive()) return;
    event.preventDefault();
    this.cardClick.emit();
  }

  protected readonly increasing = computed(() => this.variation() >= 0);
  protected readonly variationValue = computed(
    () => `${Math.round(Math.abs(this.variation()))}%`,
  );
  protected readonly bars = computed<readonly UiIndicatorBar[]>(() => {
    const periods = this.periods().slice(-4);
    const maximum = Math.max(...periods.map((period) => period.value), 1);
    return periods.map((period, index) => ({
      ...period,
      height: Math.max(8, (Math.max(0, period.value) / maximum) * 100),
      current: index === periods.length - 1,
    }));
  });
  protected readonly chartLabel = computed(() =>
    this.bars()
      .map((bar) => `${bar.label}: ${bar.valueLabel ?? bar.value}`)
      .join(', '),
  );
}
